import React from 'react';

export interface CategoryRailItem {
    /** Stable key; also the anchor target when `href` is set. */
    key: string;
    label: string;
    /** Optional count shown right-aligned, e.g. how many items in the category. */
    count?: number;
    active: boolean;
    /** Anchor rows (FAQ jump links) set `href`; filter rows set `onSelect`. */
    href?: string;
    onSelect?: () => void;
    /** Background class for a leading dot echoing the category's pill colour. */
    dotClassName?: string;
}

interface CategoryRailProps {
    items: CategoryRailItem[];
    /** Caption above the rows. */
    title?: string;
    /** Breakpoint the rail appears at; below it the page shows the list alone. */
    showFrom?: 'lg' | 'xl';
    /** Stretch to the parent's height instead of sticky-top, for viewport-locked pages. */
    fullHeight?: boolean;
}

/** Left rail for the section pages; rows are buttons when they filter, links
 * when they anchor. sticky top-0 must match the flow position beside it. */
export const CategoryRail: React.FC<CategoryRailProps> = ({
    items,
    title = 'In this section',
    showFrom = 'xl',
    fullHeight = false
}) => (
    <aside
        className={`hidden ${showFrom === 'lg' ? 'lg:flex' : 'xl:flex'} w-[268px] flex-shrink-0 ${
            fullHeight ? 'self-stretch' : 'self-start sticky top-0'
        } flex-col gap-1 p-5 border border-gray-200 rounded-xl bg-white shadow-sm`}
    >
        <span className="mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-400">
            {title}
        </span>
        {items.map((item) => {
            const className = `flex items-center gap-2.5 px-2.5 py-1.5 -mx-1 rounded-lg text-sm text-left transition-colors ${
                item.active
                    ? 'bg-gray-100 font-semibold text-gray-900'
                    : 'font-medium text-gray-700 hover:bg-gray-50'
            }`;
            const body = (
                <>
                    {item.dotClassName && (
                        <span
                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.dotClassName}`}
                        />
                    )}
                    <span className="flex-1 min-w-0 truncate">{item.label}</span>
                    {item.count !== undefined && (
                        <span className="text-xs text-gray-400 flex-shrink-0">{item.count}</span>
                    )}
                </>
            );
            return item.href ? (
                <a key={item.key} href={item.href} onClick={item.onSelect} className={className}>
                    {body}
                </a>
            ) : (
                <button key={item.key} type="button" onClick={item.onSelect} className={className}>
                    {body}
                </button>
            );
        })}
    </aside>
);

export default CategoryRail;
